import type { ReactNode } from 'react';
import { Link } from '@customerio/pluma-components/react';
import { parseTwilioError, twilioErrorDocsUrl } from '@/utils/format-error';
import { isRecord } from '@/utils/objects';

/**
 * Friendly formatting for a log's top-level `reason` value. Returns a node to render in place of
 * the raw reason, or `null` to fall back to the raw value (graceful degradation on any miss).
 * Keyed by log type first, then delivery channel:
 *  - **failed_object_journeys**: the backend-hardcoded "… Learn more <a>here</a>" reason is parsed
 *    into text + a Pluma `Link` (no `innerHTML`).
 *  - **twilio**: links the numeric error code to Twilio's error docs.
 *  - **push, stripe/api**: not formatted — fall back to the raw reason. (Push needs a large
 *    error-code map; stripe/api errors never carry a message `delivery_type` here.)
 *
 * Assumes Twilio SMS failures carry `delivery_type === 'twilio'`; if it's `'sms'` the raw reason
 * shows (safe fallback).
 */

/** A reason may be a plain string or an object with a `detail` string. */
function extractErrorKey(reason: unknown): string | null {
  if (typeof reason === 'string') return reason;
  if (isRecord(reason) && typeof reason.detail === 'string') return reason.detail;
  return null;
}

function twilioReason(reason: unknown): ReactNode | null {
  const key = extractErrorKey(reason);
  if (key == null) return null;
  const parts = parseTwilioError(key);
  if (parts == null) return null; // no code to link — fall back to the raw reason
  const { prefix, code, suffix } = parts;
  return (
    <>
      <Link href={twilioErrorDocsUrl(code)} isExternal isUnderlined>
        {prefix}
        {code}
      </Link>
      {suffix}
    </>
  );
}

// `failed_object_journeys` reasons are backend-hardcoded as "<sentence> Learn more <a href=DOCS>here</a>"
// (services/core/objects/objects_worker/worker.go). We parse out the single anchor and render text +
// a Pluma Link rather than injecting the HTML.
const ANCHOR_REGEX = /^(.*?)<a\s+href="([^"]+)"[^>]*>(.*?)<\/a>(.*)$/is;

function objectJourneysReason(reason: unknown): ReactNode | null {
  if (typeof reason !== 'string') return null;
  const match = reason.match(ANCHOR_REGEX);
  if (match == null) return null; // no anchor to convert — fall back to the raw reason
  const [, prefix, href, text, suffix] = match;
  return (
    <>
      {prefix}
      <Link href={href} isExternal isUnderlined>
        {text}
      </Link>
      {suffix}
    </>
  );
}

/** How a `reason` value should be formatted: by log type first, then delivery channel. */
type ReasonContext = { deliveryType?: string | null; logType?: string };

/** Format a log's `reason` value, or `null` to render the raw value. */
export function formatLogReason(reason: unknown, { deliveryType, logType }: ReasonContext): ReactNode | null {
  if (logType === 'failed_object_journeys') return objectJourneysReason(reason);
  if (deliveryType === 'twilio') return twilioReason(reason);
  // push / others: not formatted — fall back to the raw reason.
  return null;
}
